import { Icon } from "@may-i/mash-design-system";
import { useEffect, useRef } from "react";
import styled from "styled-components";

interface ImageLightboxProps {
  open: boolean;
  src: string;
  alt?: string;
  onClose: () => void;
}

/**
 * 인앱 이미지 확대(라이트박스). native <dialog showModal> 기반.
 *
 * 기존 "확대"는 `window.open(s3Url, "_blank")` 로 raw 이미지를 새 탭에 열었는데,
 * 브라우저가 그 origin(S3 버킷)의 /favicon.ico 를 자동 요청 → 403(버킷에 favicon 없음)
 * 이 콘솔에 찍혔다. 새 탭 대신 인앱 모달로 열어 favicon 요청 자체를 없앤다. (UX 도 개선)
 * 배경/X/Escape 로 닫힌다.
 */
export function ImageLightbox({ open, src, alt, onClose }: ImageLightboxProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
  }, [open]);

  if (!open) return null;

  return (
    <StyledDialog
      ref={dialogRef}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      // 배경(이미지 바깥) 클릭 시 닫기
      onClick={(e) => {
        if (e.target === dialogRef.current) onClose();
      }}
    >
      <CloseButton type="button" onClick={onClose} aria-label="close">
        <Icon icon="ic_close" width={20} height={20} />
      </CloseButton>
      <Image src={src} alt={alt ?? ""} />
    </StyledDialog>
  );
}

const StyledDialog = styled.dialog`
  position: fixed;
  inset: 0;
  width: 100vw;
  height: 100vh;
  max-width: 100vw;
  max-height: 100vh;
  margin: 0;
  padding: 0;
  border: none;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;

  &::backdrop {
    background: rgba(17, 17, 31, 0.72);
  }
`;

const Image = styled.img`
  max-width: 92vw;
  max-height: 92vh;
  object-fit: contain;
  border-radius: 8px;
  box-shadow: 0px 20px 40px -4px rgba(0, 0, 0, 0.4);
`;

const CloseButton = styled.button`
  position: fixed;
  top: 20px;
  right: 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.24);
  }
`;
